import {
  List,
  ListItem,
  IconButton,
  Grid,
  TextField,
  Typography,
  Box,
  Autocomplete,
  InputAdornment,
  Tooltip
} from "@mui/material";
import * as yup from "yup";
import { Plus } from "react-feather";
import { ServiceSector, Tier } from "../../models";
import { Trash } from "react-feather";
import { Employee } from "../../utils/useData";

type PlanDetailsProps = {
  form: any;
  serviceSectors: ServiceSector[];
  employees: Employee[];
};

const AmountAndCurrencySchema = yup.object().shape({
  amount: yup.number().positive().moreThan(0, "Amount must be greater than 0").required()
  // Add other fields like currency if necessary
});

const TierSchema = yup.object().shape({
  id: yup.string().notRequired(), // `undefined` is allowed so `notRequired` is used
  name: yup.string().required("Tier name is a required field").notOneOf([""], "Name cannot be empty"),
  amount: AmountAndCurrencySchema // Use the AmountAndCurrency schema here
});

export const PlanSchema = yup.object().shape({
  name: yup
    .string()
    .max(100)
    .required(),
  description: yup.string(),
  tiers: yup.array().of(TierSchema).min(1, "At least one Tier is required").required(),
  serviceSectorIds: yup.array().of(yup.string()).min(1, "At least one Category is required").required()
});

export const PlanDetailsForm = ({ form, serviceSectors, employees }: PlanDetailsProps) => {
  const handleAddTier = () => {
    const newTiers = [...form.values.tiers];
    newTiers.push({
      id: undefined,
      name: "",
      amount: {
        amount: 0
      }
    });
    form.setFieldValue("tiers", newTiers);
  };

  const handleRemoveTier = (indexToRemove: number) => {
    // Create a new array excluding the tier at the specified index
    const newTiers = form.values.tiers.filter((_: Tier, index: number) => index !== indexToRemove);

    // Update the form state with the new tiers array
    form.setFieldValue("tiers", newTiers);
  };

  const handleTierNameChange = (index: number, newName: string) => {
    const newTiers = [...form.values.tiers];
    newTiers[index].name = newName;
    form.setFieldValue("tiers", newTiers);
  };

  const handleTierAmountChange = (index: number, newAmount: number) => {
    const newTiers = [...form.values.tiers];
    newTiers[index].amount.amount = newAmount;
    form.setFieldValue("tiers", newTiers);
  };

  const renderTier = (tier: Tier, index: number) => {
    const tierNameError = form.errors.tiers?.[index]?.name;
    const tierAmountError = form.errors.tiers?.[index]?.amount?.amount;
    const canDelete = tier.id === undefined || !employees.some(e => e.accounts.some(a => a.tierId === tier.id));
    const tooltipTitle = !canDelete ? "Unable to remove the tier as it currently has employees assigned to it." : "Remove Tier";
    return (
      <ListItem
        key={index}
        secondaryAction={
          <Tooltip title={tooltipTitle}>
            <div>
              <IconButton disabled={!canDelete} edge="end" aria-label="delete" onClick={() => handleRemoveTier(index)}>
                <Trash />
              </IconButton>
            </div>
          </Tooltip>
        }
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <TextField
              variant="standard"
              fullWidth={true}
              value={tier.name}
              onChange={(e) => handleTierNameChange(index, e.target.value)}
              error={!!tierNameError}
              helperText={tierNameError}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              type="number"
              variant="standard"
              fullWidth={true}
              value={tier.amount.amount}
              onChange={(e) => handleTierAmountChange(index, parseFloat(e.target.value))}
              error={!!tierAmountError}
              helperText={tierAmountError}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
          </Grid>
        </Grid>
      </ListItem>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        <Typography sx={{ mb: 2 }} variant="subtitle1">
          Plan Name
        </Typography>
        <TextField
          value={form.values.name}
          onChange={form.handleChange}
          name="name"
          required
          fullWidth
          size="small"
          error={form.touched.name && Boolean(form.errors.name)}
          helperText={form.touched.name && form.errors.name}
        />
      </Grid>
      <Grid item xs={12} md={12} mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ mb: 2 }} variant="subtitle1">
              Contribution tiers
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={handleAddTier}>
              <Plus />
            </IconButton>
          </Box>
        </Box>
        <List>
          {form.values.tiers.map(renderTier)}
        </List>
        {/* Display an error message if there are no tiers */}
        {form.touched.tiers && form.errors.tiers && form.values.tiers.length === 0 && (
          <Typography color="error" sx={{ mt: 2 }}>
            At least one Tier is required
          </Typography>
        )}
        <Typography sx={{ mt: 4, mb: 2 }} variant="body2">
          Changes to tiers will be effective on the next renewal date
        </Typography>
      </Grid>
      <Grid item xs={12} p={2}>
        <Typography sx={{ mb: 2 }} variant="subtitle1">
          Categories
        </Typography>
        <Autocomplete
          multiple
          options={serviceSectors}
          getOptionLabel={(option) => option.name}
          defaultValue={serviceSectors.filter(sector => form.values.serviceSectorIds.includes(sector.id))}
          onChange={(event, newValue) => {
            const newIds = newValue.map((sector) => sector.id);
            form.setFieldValue("serviceSectorIds", newIds);
          }}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Categories"
            />
          )}
        />
        {/* Display an error message if there are no tiers */}
        {form.touched.serviceSectorIds && form.errors.serviceSectorIds && (
          <Typography color="error" sx={{ mt: 2 }}>
            {form.errors.serviceSectorIds}
          </Typography>
        )}
        <Typography sx={{ mt: 4, mb: 6 }} variant="body2">
          Changes to categories will be effective immediately
        </Typography>
      </Grid>
    </Grid>

  );
};
